import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Stethoscope, MapPin, Building2, Info, ArrowUpRight, Hospital } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import ProviderIdentifierPanel from '../components/ProviderIdentifierPanel';
import { api } from '../api';

function fmtInt(n) { if (n == null) return '—'; return Number(n).toLocaleString(); }
function fmtCurrency(n) { if (n == null) return '—'; return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`; }

function Stat({ value, label, accent }) {
  return (
    <div className="p-4 rounded-xl bg-white border border-[#E5DCC4]">
      <div className={`font-display text-2xl tracking-tight ${accent || 'text-[#1A2622]'}`}>{value}</div>
      <div className="mt-1 text-[10.5px] uppercase tracking-wider text-[#7A857F]">{label}</div>
    </div>
  );
}

export default function PhysicianDetailPage() {
  const { npi } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.physician(npi).then(setP).catch(e => setErr(e?.response?.data?.detail || 'Not found')).finally(() => setLoading(false));
  }, [npi]);

  if (loading) return <div className="min-h-screen"><Navbar/><main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-10"><div className="text-sm text-[#7A857F]">Loading…</div></main></div>;
  if (err || !p) return <div className="min-h-screen"><Navbar/><main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-10"><h1 className="font-display text-3xl">Physician not found</h1><p className="mt-3 text-[#7A857F]">{err}</p></main></div>;

  const name = [p.first_name, p.last_name].filter(Boolean).join(' ');
  const specialty = p.specialty_dac || p.specialty_puf;

  return (
    <div className="min-h-screen">
      <Navbar/>
      <main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-6 pb-12">
        <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Physicians', to: '/physicians' }, { label: name }]} />

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[#4A7C5E]/30 bg-white">
              <Stethoscope size={12} className="text-[#4A7C5E]"/>
              <span className="text-[11px] font-semibold uppercase tracking-wider text-[#4A7C5E]">Public Part B SNF claims</span>
            </div>
            <h1 className="mt-3 font-display text-4xl md:text-5xl font-semibold text-[#1A2622] tracking-tight leading-tight">
              {name}{p.credentials_dac ? <span className="text-[#7A857F] text-2xl">, {p.credentials_dac}</span> : ''}
            </h1>
            <div className="mt-3 text-[13.5px] text-[#3A4A44] flex flex-wrap items-center gap-x-4 gap-y-1">
              <span><strong className="text-[#1A2622]">NPI:</strong> {p.npi}</span>
              <span>{specialty || '—'}</span>
              {p.med_school && <span>· {p.med_school}{p.grad_year ? ` (${p.grad_year})` : ''}</span>}
            </div>
            {p.secondary_specialties?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {p.secondary_specialties.map((s, i) => (
                  <span key={`${s}-${i}`} className="px-2 py-0.5 rounded-md bg-[#F0E7D2] text-[#5A6660] text-[11px]">{s}</span>
                ))}
              </div>
            )}
          </div>
          <div className="lg:col-span-4 self-start">
            <div className="rounded-xl bg-white border border-[#E5DCC4] p-5">
              <div className="text-[10.5px] uppercase tracking-wider text-[#7A857F] flex items-center gap-1.5"><MapPin size={12}/> Practice address</div>
              <div className="mt-2 text-[13px] text-[#1A2622] font-medium">{p.address || '—'}</div>
              <div className="text-[12px] text-[#5A6660]">{p.city}, {p.state} {p.zip5}</div>
            </div>
          </div>
        </div>

        <section className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat value={fmtInt(p.snf_services)} label="SNF E&M services" accent="text-[#4A7C5E]" />
          <Stat value={fmtInt(p.snf_beneficiaries)} label="SNF beneficiaries seen" />
          <Stat value={fmtCurrency(p.snf_medicare_payment)} label="Medicare SNF payment" />
          <Stat value={p.linked_facilities?.length || 0} label="Likely-aligned SNFs" />
        </section>

        {/* Provider Identifier & Entity Intelligence (additive — NPPES enrichment) */}
        <div className="mt-5">
          <ProviderIdentifierPanel
            entityType="physician"
            entityId={p.npi}
            fallback={{ npi: p.npi }}
          />
        </div>

        {(p.groups || []).length > 0 && (
          <section className="mt-4 rounded-xl bg-white border border-[#E5DCC4] p-5">
            <div className="flex items-center gap-2 mb-2"><Building2 size={14} className="text-[#4A7C5E]"/><h2 className="font-display text-base font-semibold text-[#1A2622]">Group / practice affiliations</h2></div>
            <div className="space-y-1.5">
              {p.groups.map((g, i) => (
                <div key={`${g.org_pac_id || g.name}-${i}`} className="flex items-center justify-between py-1 border-b border-[#F0E7D2] last:border-0">
                  <span className="text-[12.5px] text-[#1A2622]">{g.name}</span>
                  <span className="text-[11px] text-[#7A857F]">{g.num_members ? `${fmtInt(g.num_members)} members` : ''}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="mt-4 rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
          <div className="px-5 py-4 border-b border-[#F0E7D2] flex items-center gap-2">
            <h2 className="font-display text-base font-semibold text-[#1A2622]">SNF E&M code mix (HCPCS)</h2>
            <span className="ml-auto text-[10.5px] uppercase tracking-wider text-[#7A857F]">By services rendered</span>
          </div>
          <table className="w-full text-[12.5px]">
            <thead className="bg-[#F5EFDD] text-[10.5px] uppercase tracking-wider text-[#5A6660]">
              <tr>
                <th className="text-left px-4 py-2.5">HCPCS</th>
                <th className="text-left px-4 py-2.5">Description</th>
                <th className="text-right px-4 py-2.5">Services</th>
                <th className="text-right px-4 py-2.5">Beneficiaries</th>
              </tr>
            </thead>
            <tbody>
              {(p.snf_hcpcs_mix || []).map((m) => (
                <tr key={m.hcpcs} className="border-t border-[#F0E7D2]">
                  <td className="px-4 py-2.5 font-semibold tabular-nums">{m.hcpcs}</td>
                  <td className="px-4 py-2.5 text-[#5A6660]">{HCPCS_LABELS[m.hcpcs] || ''}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{fmtInt(m.services)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{fmtInt(m.beneficiaries)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {p.linked_facilities?.length > 0 && (
          <section className="mt-4 rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
            <div className="px-5 py-4 border-b border-[#F0E7D2] flex items-center gap-2">
              <Building2 size={14} className="text-[#4A7C5E]"/>
              <h2 className="font-display text-base font-semibold text-[#1A2622]">Likely affiliated facilities</h2>
              <span className="ml-auto text-[10.5px] uppercase tracking-wider text-[#7A857F]">Sorted by alignment score</span>
            </div>
            <ul>
              {p.linked_facilities.map((f, i) => (
                <li key={`${f.ccn}-${i}`} className="px-4 py-2.5 flex items-center justify-between gap-3 border-t border-[#F0E7D2] first:border-t-0 hover:bg-[#FBF6EC]">
                  <div className="min-w-0">
                    <Link to={`/facilities/${f.ccn}`} className="text-[12.5px] text-[#1A2622] font-medium hover:text-[#2D5F4F] truncate">{f.facility_name}</Link>
                    <div className="text-[10.5px] text-[#7A857F]">{f.facility_city}, {f.facility_state} · {f.confidence} confidence · {f.reason?.replace(/_/g, ' ')}</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-[12px] font-semibold text-[#4A7C5E] tabular-nums">{f.alignment_score?.toFixed(0)}</span>
                    <ArrowUpRight size={13} className="text-[#A8B0AA]"/>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {p.hospital_affiliations?.length > 0 && (
          <section className="mt-4 rounded-xl bg-white border border-[#E5DCC4] overflow-hidden" data-testid="phys-hosp-affiliations">
            <div className="px-5 py-4 border-b border-[#F0E7D2] flex items-center gap-2 flex-wrap">
              <Hospital size={14} className="text-[#34538F]"/>
              <h2 className="font-display text-base font-semibold text-[#1A2622]">Hospital affiliations</h2>
              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#E0EAF8] text-[#34538F]">CMS DAC confirmed</span>
              <span className="ml-auto text-[10.5px] uppercase tracking-wider text-[#7A857F]">{p.hospital_affiliations.length} hospitals</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead className="bg-[#F5EFDD] text-[10.5px] uppercase tracking-wider text-[#5A6660]">
                  <tr>
                    <th className="text-left px-4 py-2.5">Hospital</th>
                    <th className="text-left px-4 py-2.5">Type / Ownership</th>
                    <th className="text-left px-4 py-2.5">City / State</th>
                    <th className="text-right px-4 py-2.5">Rating</th>
                  </tr>
                </thead>
                <tbody>
                  {p.hospital_affiliations.map((h) => (
                    <tr key={h.ccn} className="border-t border-[#F0E7D2] hover:bg-[#FBF6EC]" data-testid={`phys-hosp-${h.ccn}`}>
                      <td className="px-4 py-2.5">
                        {h.name
                          ? <Link to={`/hospitals/${h.ccn}`} className="text-[#1A2622] font-medium hover:text-[#34538F]">{h.name}</Link>
                          : <span className="text-[#7A857F]">CCN {h.ccn}</span>}
                        <div className="text-[10.5px] text-[#7A857F]">CCN {h.ccn}</div>
                      </td>
                      <td className="px-4 py-2.5 text-[#3A4A44]">
                        <div className="truncate max-w-[200px]" title={h.type}>{h.type || '—'}</div>
                        <div className="text-[10.5px] text-[#7A857F] truncate max-w-[200px]" title={h.ownership}>{h.ownership || ''}</div>
                      </td>
                      <td className="px-4 py-2.5 text-[#3A4A44]">{h.city ? `${h.city}, ${h.state}` : '—'}</td>
                      <td className="px-4 py-2.5 text-right">
                        {h.rating != null
                          ? <span className="font-semibold text-[#A06628]">{h.rating}<span className="text-[#D4A85C] ml-0.5">★</span></span>
                          : <span className="text-[#A8B0AA]">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {p.snf_affiliations?.length > 0 && (
          <section className="mt-4 rounded-xl bg-white border border-[#E5DCC4] overflow-hidden" data-testid="phys-snf-affiliations">
            <div className="px-5 py-4 border-b border-[#F0E7D2] flex items-center gap-2 flex-wrap">
              <Building2 size={14} className="text-[#2D5F4F]"/>
              <h2 className="font-display text-base font-semibold text-[#1A2622]">Nursing-home affiliations</h2>
              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#E5F0EA] text-[#2D5F4F]">CMS DAC confirmed</span>
              <span className="ml-auto text-[10.5px] uppercase tracking-wider text-[#7A857F]">{p.snf_affiliations.length} SNFs</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead className="bg-[#F5EFDD] text-[10.5px] uppercase tracking-wider text-[#5A6660]">
                  <tr>
                    <th className="text-left px-4 py-2.5">SNF</th>
                    <th className="text-left px-4 py-2.5">City / State</th>
                    <th className="text-right px-4 py-2.5">Beds</th>
                    <th className="text-right px-4 py-2.5">Rating</th>
                  </tr>
                </thead>
                <tbody>
                  {p.snf_affiliations.map((f) => (
                    <tr key={f.ccn} className="border-t border-[#F0E7D2] hover:bg-[#FBF6EC]" data-testid={`phys-snf-${f.ccn}`}>
                      <td className="px-4 py-2.5">
                        <Link to={`/facilities/${f.ccn}`} className="text-[#1A2622] font-medium hover:text-[#2D5F4F]">{f.name || `CCN ${f.ccn}`}</Link>
                        <div className="text-[10.5px] text-[#7A857F]">CCN {f.ccn}</div>
                      </td>
                      <td className="px-4 py-2.5 text-[#3A4A44]">{f.city ? `${f.city}, ${f.state}` : '—'}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{fmtInt(f.beds)}</td>
                      <td className="px-4 py-2.5 text-right">
                        {f.overall_rating != null
                          ? <span className="font-semibold text-[#A06628]">{f.overall_rating}<span className="text-[#D4A85C] ml-0.5">★</span></span>
                          : <span className="text-[#A8B0AA]">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        <div className="mt-5 flex items-start gap-2 text-[11.5px] text-[#A8B0AA] leading-relaxed">
          <Info size={12} className="mt-0.5 shrink-0"/>
          <span>
            <strong>Methodology:</strong> {p.methodology}{' '}
            <strong>Source:</strong> {p.source}.{' '}
            <strong>Secondary:</strong> {p.secondary_source}.{' '}
            {p.affiliation_source && <><strong>Affiliations:</strong> {p.affiliation_source}.{' '}</>}
            {p.updated_at && <><strong>Last updated:</strong> {new Date(p.updated_at).toLocaleDateString()}.{' '}</>}
          </span>
        </div>
      </main>
      <Footer/>
    </div>
  );
}

const HCPCS_LABELS = {
  '99304': 'Initial nursing facility care, level 1',
  '99305': 'Initial nursing facility care, level 2',
  '99306': 'Initial nursing facility care, level 3',
  '99307': 'Subsequent nursing facility care, level 1',
  '99308': 'Subsequent nursing facility care, level 2',
  '99309': 'Subsequent nursing facility care, level 3',
  '99310': 'Subsequent nursing facility care, level 4',
  '99315': 'Nursing facility discharge management ≤ 30 min',
  '99316': 'Nursing facility discharge management > 30 min',
};
